import { Highlights } from '@/components/modules/MarketList/UserMarket';
import { NopPositionRow } from '@/components/trader-modules/Blotters/NopPositionTable';
import { GroupBy } from '@/components/trader-modules/Blotters/PositionGroupSwitch';
import { getCurrency } from '@/utils/hooks/useCurrency';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { GetColorIndexesReturn, getUniversalInstrument, useInstrument } from '@/utils/hooks/useInstrument';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import usePreviousState from '@/utils/hooks/usePreviousState';
import { CellContext } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { MdChevronRight, MdClose, MdInfoOutline } from 'react-icons/md';

interface NopPositionColumnsParams {
    lang: UseLanguageReturn;
    baseCurrency: string;
    onOrderModal: (row: NopPositionRow) => void;
    groupBy: GroupBy;
    formatQty: Intl.NumberFormat;
    formatConvertedQty: Intl.NumberFormat;
    setInstrumentInfoContext: any;
    handleOpenContextMenu: any;
}

const shouldShowComponent = (groupBy: GroupBy, depth: number, children: any) => {
    if (!(groupBy === 'Products' && depth === 0)) return children;
    return undefined;
};

const formatterCache = new Map();

function getFormatter(baseFormatter, min, max) {
    const { locale, ...opts } = baseFormatter.resolvedOptions();
    const key = `${locale}-${min}-${max}`;

    if (!formatterCache.has(key)) {
        formatterCache.set(
            key,
            new Intl.NumberFormat(locale, {
                ...opts,
                minimumFractionDigits: min,
                maximumFractionDigits: max
            })
        );
    }

    return formatterCache.get(key);
}

export const nopPositionColumns = (params: NopPositionColumnsParams) => {
    const {
        lang,
        baseCurrency,
        onOrderModal,
        groupBy,
        formatQty,
        formatConvertedQty,
        setInstrumentInfoContext,
        handleOpenContextMenu
    } = params;

    const columns = [
        {
            size: 180,
            accessorKey: 'tableInstrument',
            header: ({ table }) => {
                return (
                    <div className="flex space-x-1 items-center truncate">
                        <button
                            onClick={(e) => {
                                table.toggleAllRowsExpanded();
                                e.stopPropagation();
                            }}
                            title={table.getIsAllRowsExpanded() ? 'Collapse all rows' : 'Expand all rows'}
                            className="relative transform scale-125 left-0">
                            <MdChevronRight
                                className={cn('h-3.5 w-3.5 font-bold text-green duration-200', {
                                    ['rotate-90 ']: table.getIsAllRowsExpanded()
                                })}
                                aria-hidden
                            />
                        </button>

                        <span className="truncate">{lang.blotters.instrument}</span>
                    </div>
                );
            },
            cell: ({ row, getValue }: CellContext<NopPositionRow, any>) => {
                const value = getValue();
                let showVal = value;
                if (value === 'CFDs on Futures') showVal = 'Futures CFDs';
                return (
                    <div
                        className="flex items-center gap-1"
                        style={{
                            paddingLeft: `${row.getCanExpand() ? row.depth * 1 : row.depth * 1.5}rem`
                        }}>
                        <div className={`flex space-x-1`}>
                            {row.getCanExpand() && (
                                <button title={row.getIsExpanded() ? 'Collapse current row' : 'Expand current row'}>
                                    <MdChevronRight
                                        className={cn('h-3.5 w-3.5 text-green duration-200', {
                                            ['rotate-90']: row.getIsExpanded()
                                        })}
                                        aria-hidden
                                    />
                                </button>
                            )}
                            {/* TBA - Translations */}
                            {value ? <span>{showVal}</span> : row.depth === 0 ? <span>Others</span> : ''}
                        </div>
                        {row.getCanExpand() && !(groupBy === 'Products' && row.depth === 0) && (
                            <div
                                title="Instrument Info"
                                className={cn(
                                    'h-full flex items-center p-1 cursor-pointer hover:text-brand-green-light',
                                    {}
                                )}
                                onDrag={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setInstrumentInfoContext(row.original.market);
                                    handleOpenContextMenu(e);
                                }}>
                                <MdInfoOutline className="w-3 h-3" />
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'type',
            header: lang.blotters.type,
            cell: ({ row, getValue }: CellContext<NopPositionRow, any>) =>
                shouldShowComponent(groupBy, row.depth, getValue())
        },
        {
            accessorKey: 'amount',
            header: lang.blotters.netQtyPosition,
            cell: ({ row, getValue }: CellContext<NopPositionRow, any>) => {
                const value = getValue();
                const ccy1 = row.original.instrument.split('/')[0];
                let effectiveCurrency = ccy1;
                if (row.original.type === 'Futures CFD') effectiveCurrency = row.original.ccy2 || 'USD';
                const { show } = getCurrency('');

                return shouldShowComponent(
                    groupBy,
                    row.depth,
                    <div
                        className={cn('text-sm', {
                            'text-brand-green': value > 0,
                            'text-brand-red': value < 0
                        })}>
                        {isNaN(value) || value === 0 ? '-' : `${show} ${formatQty.format(value)}`}
                    </div>
                );
            }
        },
        {
            size: 50,
            minSize: 50,
            accessorKey: 'action',
            header: lang.blotters.close,
            cell: ({ row, getValue }: CellContext<NopPositionRow, any>) => {
                const original = row.original;

                return (
                    original.amount !== 0 &&
                    shouldShowComponent(
                        groupBy,
                        row.depth,
                        <div
                            className={cn('flex justify-center items-center mr-1.5', {
                                'bg-brand-red hover:bg-brand-red-dark': original.amount !== 0,
                                'bg-brand-neutral-600 cursor-not-allowed': original.amount === 0
                            })}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (original.amount !== 0)
                                    onOrderModal({ ...original, netQtyPosition: original.amount } as any);
                            }}>
                            <MdClose className="w-4 h-4" />
                        </div>
                    )
                );
            }
        },
        {
            accessorKey: 'openPrice',
            header: lang.blotters.openPrice,
            cell: ({ row, getValue }: CellContext<NopPositionRow, any>) => {
                const value = getValue();
                let formatter = formatConvertedQty;

                if (!(groupBy === 'Products' && row.depth === 0)) {
                    const instrumentConfig = getUniversalInstrument(row.original.instrument);
                    formatter = getFormatter(formatConvertedQty, 0, (instrumentConfig?.price_decimals || 0) + 2);
                }

                return shouldShowComponent(
                    groupBy,
                    row.depth,
                    <div className="text-sm">{isNaN(value) || value === 0 ? '-' : formatter.format(value)}</div>
                );
            }
        },
        {
            accessorKey: 'currentPrice',
            header: lang.blotters.currentPrice,
            cell: ({ row, getValue }: CellContext<NopPositionRow, any>) => {
                const value = getValue();

                const prevMid = usePreviousState(value);
                const { formatPip, formatHighlights } = useInstrument(row.original.instrument);
                const formattedMid = useMemo(() => value && formatPip(value), [value]);
                const [midHighlights, setMidHighlights] = useState<GetColorIndexesReturn | undefined>();

                useDidUpdate(() => {
                    if (value && prevMid) setMidHighlights(formatHighlights(prevMid, value, midHighlights));
                }, [value]);

                return shouldShowComponent(
                    groupBy,
                    row.depth,
                    <div className="text-sm">
                        {isNaN(value) || row.original.amount === 0 ? (
                            '-'
                        ) : (
                            <Highlights ticker={formattedMid} highlights={midHighlights} />
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'unrealisedPnl',
            header: lang.blotters.unrealisedPnl,
            width: 150,
            cell: ({ row, getValue }: CellContext<NopPositionRow, any>) => {
                const { value } = getValue();
                const ccy2 = row.original.instrument.split('/')[1];
                let effectiveCurrency = ccy2;
                if (row.original.type === 'Futures CFD') effectiveCurrency = 'USD';
                const { show, order_decimals } = getCurrency(effectiveCurrency);

                const number = new Intl.NumberFormat(formatQty.resolvedOptions().locale, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: Math.max(0, order_decimals - 2)
                });

                let formattedValue = '-';
                if (!isNaN(value)) {
                    formattedValue = number.format(value);
                }

                return shouldShowComponent(
                    groupBy,
                    row.depth,
                    <div
                        className={cn('text-sm', {
                            'text-brand-green': value > 0 && formattedValue !== '0',
                            'text-brand-red': value < 0 && formattedValue !== '-0',
                            'font-semibold': groupBy === 'Products' && row.depth === 0
                        })}>
                        {isNaN(value) ? '-' : `${show} ${formattedValue === '-0' ? '0' : formattedValue}`}
                    </div>
                );
            }
        },
        {
            accessorKey: 'unrealisedPnlInBaseCurrency',
            header: `${lang.blotters.unrealisedPnl} ${baseCurrency}`,
            width: 150,
            cell: ({ row, getValue }: CellContext<NopPositionRow, any>) => {
                const { value, currency } = getValue();
                const { show, order_decimals } = getCurrency(currency);

                const number = new Intl.NumberFormat(formatQty.resolvedOptions().locale, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: Math.max(0, order_decimals - 2)
                });

                let formattedValue = '-';
                if (!isNaN(value)) {
                    formattedValue = number.format(value);
                }

                return (
                    <div
                        className={cn('text-sm', {
                            'text-brand-green': value > 0 && formattedValue !== '0',
                            'text-brand-red': value < 0 && formattedValue !== '-0',
                            'font-semibold': groupBy === 'Products' && row.depth === 0
                        })}>
                        {value === null
                            ? '-'
                            : isNaN(value)
                            ? '-'
                            : `${show} ${formattedValue === '-0' ? '0' : formattedValue}`}
                    </div>
                );
            }
        }
        // {
        //     accessorKey: 'settlementDate',
        //     header: 'Value Date',
        //     cell: ({ row, getValue }: CellContext<NopPositionRow, any>) =>
        //         shouldShowComponent(groupBy, row.depth, getValue())
        // }
    ];
    return groupBy === 'None' ? columns : columns.filter((column) => column.accessorKey !== 'type');
};
